/**
 * Making a body the compiler did **not** author readable — for display only.
 *
 * `compile()` indents its own output, so an editor-authored body needs nothing.
 * Legacy and hand-edited bodies are the problem, and they are most of what a real
 * partner has: complete HTML documents, table soup, Twig hidden in HTML comments,
 * frequently with the whole thing on very few lines.
 *
 * **This never writes back.** The result is shown behind a *Format* toggle and the
 * stored bytes are untouched — reformatting hand-written Twig to store it would be
 * data loss for a cosmetic gain, and there is no runtime formatter to be exact
 * with anyway (`prettier` is a devDependency, and has no Twig support).
 *
 * ### Why it only breaks at block-level boundaries
 *
 * Whitespace between *inline* elements is significant in HTML: putting
 * `<span>a</span> <span>b</span>` on two lines changes how it renders. A formatter
 * that reflows freely would therefore show the user something that does not render
 * like the source it claims to be — worse than an ugly view, because they might
 * copy it back out.
 *
 * So this only ever breaks before/after **Twig tags** and a known list of
 * **block-level** HTML tags, and never touches text, inline elements, or the inside
 * of `<pre>` / `<textarea>` / `<script>` / `<style>`.
 */

/** One level of indentation, matching the compiler's own. */
const INDENT = '  ';

/**
 * Tags that already imply a line break in any hand-formatted HTML. Deliberately
 * excludes inline elements (`a`, `span`, `strong`, `img`, …) — see the note above.
 */
const BLOCK_TAGS = new Set([
  'html', 'head', 'body', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'div', 'p', 'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'section', 'article', 'header',
  'footer', 'nav', 'aside', 'main', 'center', 'blockquote', 'figure', 'form',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'style', 'title', 'meta', 'link',
]);

/** Never need a closing tag, so they must not open an indent level. */
const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta',
  'param', 'source', 'track', 'wbr',
]);

/** Their content is significant to the byte; passed through untouched. */
const OPAQUE_TAGS = ['pre', 'textarea', 'script', 'style'];

/** Twig tags that open a scope. `set` only does when it has an `endset`. */
const TWIG_OPENERS = new Set([
  'if', 'for', 'block', 'embed', 'macro', 'apply', 'autoescape', 'filter',
  'spaceless', 'verbatim', 'sandbox', 'with',
]);

/** Twig tags that continue a scope without changing its depth. */
const TWIG_MIDDLES = new Set(['else', 'elseif']);

interface Token {
  text: string;
  /** Depth change applied *before* this token is placed. */
  before: number;
  /** Depth change applied *after* it. */
  after: number;
  /** Start this token on a new line. */
  breakBefore: boolean;
  /** End the line after it. */
  breakAfter: boolean;
}

/**
 * Everything that has to be recognised as one unit. Order matters: the opaque
 * elements and comments come first so their innards are never re-tokenised.
 */
const TOKEN_PATTERN = new RegExp(
  [
    // Opaque elements, content included.
    `<(?:${OPAQUE_TAGS.join('|')})\\b[^>]*>[\\s\\S]*?</(?:${OPAQUE_TAGS.join('|')})\\s*>`,
    '<!--[\\s\\S]*?-->', // HTML comment (legacy bodies hide Twig in these)
    '\\{#[\\s\\S]*?#\\}', // Twig comment
    '\\{%-?[\\s\\S]*?-?%\\}', // Twig tag
    '\\{\\{[\\s\\S]*?\\}\\}', // Twig output
    '<!\\[[^\\]]*\\]>', // MSO conditional open
    '<!\\[endif\\]-->', // MSO conditional close
    '<!doctype[^>]*>',
    '</?[a-zA-Z][^>]*>', // HTML tag
  ].join('|'),
  'gi',
);

function twigKeyword(tag: string): string {
  return (
    tag
      .replace(/^\{%-?\s*/, '')
      .match(/^[a-zA-Z_]+/)?.[0]
      .toLowerCase() ?? ''
  );
}

function htmlTagName(tag: string): string {
  return tag.replace(/^<\/?/, '').match(/^[a-zA-Z0-9]+/)?.[0].toLowerCase() ?? '';
}

function classify(text: string): Token {
  const token: Token = {
    text,
    before: 0,
    after: 0,
    breakBefore: false,
    breakAfter: false,
  };

  // Opaque elements and comments: one unit, on their own line, depth unchanged.
  if (/^<!--/.test(text) || /^\{#/.test(text)) {
    return { ...token, breakBefore: true, breakAfter: true };
  }
  if (new RegExp(`^<(?:${OPAQUE_TAGS.join('|')})\\b`, 'i').test(text)) {
    return { ...token, breakBefore: true, breakAfter: true };
  }
  if (/^<!doctype/i.test(text)) {
    return { ...token, breakBefore: true, breakAfter: true };
  }

  if (/^\{\{/.test(text)) return token; // inline output — never breaks a line

  if (/^\{%/.test(text)) {
    const keyword = twigKeyword(text);
    if (keyword.startsWith('end')) {
      return { ...token, before: -1, breakBefore: true, breakAfter: true };
    }
    if (TWIG_MIDDLES.has(keyword)) {
      return { ...token, before: -1, after: 1, breakBefore: true, breakAfter: true };
    }
    if (TWIG_OPENERS.has(keyword)) {
      return { ...token, after: 1, breakBefore: true, breakAfter: true };
    }
    // `set`, `include`, `import`, `do`, … — standalone statements.
    return { ...token, breakBefore: true, breakAfter: true };
  }

  if (/^<\//.test(text)) {
    const name = htmlTagName(text);
    return BLOCK_TAGS.has(name)
      ? { ...token, before: -1, breakBefore: true, breakAfter: true }
      : token;
  }

  if (/^</.test(text)) {
    const name = htmlTagName(text);
    if (!BLOCK_TAGS.has(name)) return token;
    const selfClosing = /\/>\s*$/.test(text) || VOID_TAGS.has(name);
    return selfClosing
      ? { ...token, breakBefore: true, breakAfter: true }
      : { ...token, after: 1, breakBefore: true, breakAfter: true };
  }

  return token;
}

/**
 * Re-indents `source` for reading. Idempotent: formatting already-formatted
 * output returns the same string, which is what makes the toggle safe to flip.
 */
export function prettifyTwigSource(source: string): string {
  if (!source.trim()) return source;

  const tokens: Token[] = [];
  let cursor = 0;

  for (const match of source.matchAll(TOKEN_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      /**
       * Text between constructs: whitespace runs collapse to one space, but the
       * text is **not trimmed** — a space between inline elements is significant
       * (`</strong> and <a>`), and trimming turned that into
       * `</strong>and<a>`, which renders differently from the source this claims to
       * show. Whitespace-only text is kept for the same reason; it is removed only
       * at a line edge, where a break has already established block context.
       */
      const between = source.slice(cursor, index).replace(/\s+/g, ' ');
      if (between.length > 0) tokens.push(classify(between));
    }
    tokens.push(classify(match[0]));
    cursor = index + match[0].length;
  }

  const tail = source.slice(cursor).replace(/\s+/g, ' ');
  if (tail.length > 0) tokens.push(classify(tail));

  const lines: string[] = [];
  let current = '';
  let depth = 0;

  // Trimming happens only at the edges of lines this function creates, where a
  // break has already put us in block context and whitespace is insignificant.
  const flush = () => {
    const line = current.trimEnd();
    if (line.length > 0) lines.push(INDENT.repeat(Math.max(depth, 0)) + line);
    current = '';
  };

  for (const token of tokens) {
    if (token.breakBefore) flush();
    // The dedent applies to the line this token starts, so it lands before the
    // line is written rather than after.
    if (token.before !== 0 && current.length === 0) {
      depth = Math.max(depth + token.before, 0);
    }
    current += current.length > 0 ? token.text : token.text.trimStart();
    if (token.breakAfter) {
      flush();
      depth = Math.max(depth + token.after, 0);
    } else if (token.after !== 0) {
      depth = Math.max(depth + token.after, 0);
    }
  }
  flush();

  return lines.join('\n');
}
